import React, { useState, useEffect, useRef } from "react";
import { FiTrash2, FiSend } from "react-icons/fi";
import api from "../api";
import ReactGA from "react-ga4";
import "./style/SocialCommentsModal.css";

export default function SocialCommentsModal({
  isOpen,
  onClose,
  photoId,
  eventId,
  authenticated,
  currentUserId,
  onCommentAdded,
  onCommentDeleted,
}) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  // @mention autocomplete
  const [mentions, setMentions] = useState([]); // [{user_id, display_name}]
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);

  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && photoId) fetchComments();
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 300);
  }, [isOpen, photoId]);

  useEffect(() => {
    if (!isOpen) {
      setText("");
      setMentions([]);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [isOpen]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [comments]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  async function fetchComments() {
    try {
      const res = await api.get(`/social/comments/${photoId}`);
      if (res.data.success) setComments(res.data.comments);
    } catch {}
  }

  // Detect @mention trigger in comment input
  function handleTextChange(e) {
    const val = e.target.value;
    setText(val);

    const match = val.match(/@(\w*)$/);
    if (match && eventId) {
      const q = match[1];
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        try {
          const res = await api.get("/social/taggable", { params: { eid: eventId, q } });
          if (res.data.success && res.data.suggestions.length > 0) {
            setSuggestions(res.data.suggestions);
            setShowSuggestions(true);
          } else {
            setShowSuggestions(false);
          }
        } catch {
          setShowSuggestions(false);
        }
      }, 280);
    } else {
      clearTimeout(debounceRef.current);
      setShowSuggestions(false);
    }
  }

  function handleSelectMention(user) {
    const newText = text.replace(/@(\w*)$/, `@${user.display_name} `);
    setText(newText);
    if (!mentions.find((m) => m.user_id === user.user_id)) {
      setMentions((prev) => [...prev, user]);
    }
    setShowSuggestions(false);
    setSuggestions([]);
    inputRef.current?.focus();
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!text.trim() || loading) return;
    setLoading(true);
    try {
      const params = { text: text.trim() };
      if (mentions.length > 0) {
        params.tagged_user_ids = mentions.map((m) => m.user_id).join(",");
      }
      const res = await api.post(`/social/comment/${photoId}`, null, { params });
      if (res.data.success) {
        ReactGA.event({ category: 'engagement', action: 'social_comment' });
        setComments((prev) => [...prev, res.data.comment]);
        setText("");
        setMentions([]);
        onCommentAdded?.();
      }
    } catch {}
    setLoading(false);
  }

  async function handleDelete(commentId) {
    try {
      await api.delete(`/social/comment/${commentId}`);
      setComments((prev) => prev.filter((c) => c.social_comment_id !== commentId));
      onCommentDeleted?.();
    } catch {}
  }

  function formatDate(dateStr) {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMin = Math.floor((now - d) / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }

  function initials(name) {
    if (!name) return "?";
    return name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);
  }

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="scm-backdrop" onClick={onClose} />

      {/* Drawer */}
      <div className="scm-drawer">
        {/* Handle */}
        <div className="scm-handle-row">
          <div className="scm-handle" />
        </div>

        {/* Header */}
        <div className="scm-header">
          <span className="scm-header__title">Comments</span>
          <button className="scm-header__close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Comment list */}
        <div className="scm-list">
          {comments.length === 0 && (
            <div className="scm-empty">
              <span className="scm-empty__icon">💬</span>
              <p>No comments yet. Be the first!</p>
            </div>
          )}

          {comments.map((c) => (
            <div key={c.social_comment_id} className="scm-item">
              <div className="scm-item__avatar">{initials(c.author.display_name)}</div>
              <div className="scm-item__body">
                <div className="scm-item__top">
                  <span className="scm-item__author">{c.author.display_name}</span>
                  <span className="scm-item__date">{formatDate(c.created_at)}</span>
                </div>
                <p className="scm-item__text">{c.text}</p>
              </div>
              {c.is_own && (
                <button
                  className="scm-item__delete"
                  onClick={() => handleDelete(c.social_comment_id)}
                  title="Delete"
                >
                  <FiTrash2 size={13} />
                </button>
              )}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        {/* Input area */}
        {authenticated ? (
          <form className="scm-input-area" onSubmit={handleSubmit}>
            {/* @mention autocomplete dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="scm-mention-dropdown">
                {suggestions.map((s) => (
                  <div
                    key={s.user_id}
                    className="scm-mention-item"
                    onMouseDown={(e) => { e.preventDefault(); handleSelectMention(s); }}
                  >
                    <div className="scm-mention-avatar">{initials(s.display_name)}</div>
                    <span>{s.display_name}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Mention chips */}
            {mentions.length > 0 && (
              <div className="scm-mention-chips">
                {mentions.map((m) => (
                  <span key={m.user_id} className="scm-mention-chip">
                    @{m.display_name}
                  </span>
                ))}
              </div>
            )}

            {/* Text input + send */}
            <div className="scm-input-row">
              <input
                ref={inputRef}
                className="scm-input"
                placeholder="Write a comment… type @ to mention"
                value={text}
                onChange={handleTextChange}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setShowSuggestions(false);
                }}
                maxLength={500}
                autoComplete="off"
              />
              <button
                type="submit"
                className="scm-send-btn"
                disabled={!text.trim() || loading}
                aria-label="Post"
              >
                <FiSend size={17} />
              </button>
            </div>
          </form>
        ) : (
          <div className="scm-input-row">
            <p className="scm-login-hint">Log in to leave a comment.</p>
          </div>
        )}
      </div>
    </>
  );
}
